import {
  faArrowUpRightFromSquare,
  faRemove,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BorrowerDto } from '../../../../openapi/atlantis';
import { FileResponse } from '../../../../openapi/dropbox';
import { deleteFileFromDropbox } from '../../../../slices/DropboxSlice';
import { useAppDispatch, useAppSelector } from '../../../../slices/store';
import { bytesToSize } from '../../../../utils/FileUtils';
import DefaultLoader from '../../../DefaultLoader';

interface FileRowV2Props {
  file: FileResponse;
  fileIcon?: React.ReactElement;
  fileUrl?: string;
  onDelete?: () => void;
}

const FileRowV2: React.FC<FileRowV2Props> = ({
  file,
  fileIcon,
  fileUrl,
  onDelete,
}) => {
  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const {
    auth: { authUserDetail },
    loan: { borrowerDetail },
  } = useAppSelector((state) => ({
    auth: state.auth,
    loan: state.loan,
  }));

  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const borrowerData = useMemo<BorrowerDto>(() => {
    return applicationId
      ? ({
          id: authUserDetail?.id,
          dropboxId: authUserDetail?.dropboxId,
        } as BorrowerDto)
      : borrowerDetail!;
  }, [applicationId, authUserDetail, borrowerDetail]);

  const { filename, length: fileSize, createdAt } = file;

  const handleFileDelete = useCallback(async () => {
    setIsDeletingFile(true);
    if (file?.id) {
      await dispatch(deleteFileFromDropbox(borrowerData, file?.id!));
    }
    onDelete?.();
    setIsDeletingFile(false);
  }, [dispatch, borrowerData, file, onDelete]);

  return (
    <div className='flex w-full p-3 justify-center items-center rounded-lg bg-reskin-regent-200 hover:bg-reskin-grey-100'>
      <div className='flex items-center justify-between gap-x-2 lg:gap-x-6 w-full'>
        {fileIcon && (
          <div className='flex p-2 justify-center items-center rounded border border-reskin-regent-400 bg-white'>
            {fileIcon}
          </div>
        )}

        <div className='flex flex-col flex-1 items-start gap-y-1 md:gap-y-[6px]'>
          <div className='flex items-center gap-x-2'>
            <span className='font-inter font-medium md:font-semibold text-sm md:text-base text-primary-dark'>
              {filename}
            </span>
            {fileUrl && (
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                fontSize={16}
                className='text-reskin-primary-blue cursor-pointer'
                onClick={() => window.open(fileUrl, '_blank')}
              />
            )}
          </div>

          {createdAt && (
            <span className='font-inter text-xs md:text-sm font-light md:font-medium text-reskin-grey-400'>
              {fileSize ? `${bytesToSize(fileSize)} | ` : ''}
              Uploaded:{' '}
              {DateTime.fromMillis(createdAt).toFormat('MMM dd, yyyy, hh:mm a')}
            </span>
          )}
        </div>

        <div className='w-8 h-8 flex items-center justify-center'>
          {isDeletingFile ? (
            <DefaultLoader iconSize={16} noPadding />
          ) : (
            <div className='flex items-center justify-center rounded-full bg-white border-[0.5px] border-reskin-regent-300 p-1 w-6 h-6'>
              <FontAwesomeIcon
                icon={faRemove}
                fontSize={16}
                className='text-reskin-grey-500 cursor-pointer'
                onClick={handleFileDelete}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileRowV2;
